import type {
  Agent,
  AgentStatusResponse,
  TenantsApiResponse,
  TenantConfig,
  WorkspaceFile,
  WorkspaceFileContent,
  RuleFile,
  RuleFileContent,
  KpiSourcesResponse,
  KpiSourceDetail,
  ConvertHistoryResponse,
} from "./types";
import { request, mapStatus } from "./base";

// --- Tenants ---

export async function fetchTenants(): Promise<Agent[]> {
  const data = await request<TenantsApiResponse>("/api/tenants");
  return data.tenants
    .filter((t) => t.enabled)
    .map((t) => ({
      tenantCode: t.id,
      name: t.name,
      nameKorean: t.nameKorean,
      emoji: t.identity.emoji,
      status: mapStatus(t.status),
      category: (t.category as "customer" | "ops") || "customer",
    }));
}

export async function fetchStatus(tenantCode: string): Promise<AgentStatusResponse> {
  return request<AgentStatusResponse>(`/api/tenants/${tenantCode}/status`);
}

export function startAgent(tenantCode: string): Promise<void> {
  return request(`/api/tenants/${tenantCode}/start`, { method: "POST" });
}

export function stopAgent(tenantCode: string): Promise<void> {
  return request(`/api/tenants/${tenantCode}/stop`, { method: "POST" });
}

// --- Conversation History ---

export interface ConversationSummary {
  convId: string;
  messageCount: number;
  lastTimestamp: number;
  name?: string;
  firstMessage?: string;
}

export interface ConversationMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

export async function fetchConversations(tenantId: string): Promise<ConversationSummary[]> {
  const data = await request<{ conversations: ConversationSummary[] }>(`/api/tenants/${tenantId}/conversations`);
  return data.conversations;
}

export async function renameConversation(tenantId: string, convId: string, name: string): Promise<void> {
  await request(`/api/tenants/${tenantId}/conversations/${encodeURIComponent(convId)}`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export async function deleteConversation(tenantId: string, convId: string): Promise<void> {
  await request(`/api/tenants/${tenantId}/conversations/${encodeURIComponent(convId)}`, {
    method: "DELETE",
  });
}

export async function fetchConversationsPaginated(
  tenantId: string,
  limit: number,
  offset: number,
): Promise<{ conversations: ConversationSummary[]; total: number; limit: number; offset: number }> {
  return request(`/api/tenants/${tenantId}/conversations?limit=${limit}&offset=${offset}`);
}

export async function fetchConversationMessages(tenantId: string, convId: string): Promise<ConversationMessage[]> {
  const data = await request<{ messages: ConversationMessage[] }>(`/api/tenants/${tenantId}/conversations/${encodeURIComponent(convId)}`);
  return data.messages;
}

// --- Continue Conversation (이어서 대화) ---

export async function continueConversation(
  tenantId: string,
  convId: string,
  message: string,
): Promise<{ response: string; claudeSessionId: string | null }> {
  return request(`/api/tenants/${tenantId}/conversations/${encodeURIComponent(convId)}/continue`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

// --- Start New Conversation (새 대화 시작 — fire-and-forget) ---

export async function startConversation(
  tenantId: string,
  message: string,
  conversationName?: string,
): Promise<{ convId: string; response: string | null; status: "processing" | "completed"; claudeSessionId: string | null }> {
  return request(`/api/tenants/${tenantId}/conversations`, {
    method: "POST",
    body: JSON.stringify({ message, conversationName }),
  });
}

// --- Batch Pre-scale (배치 시작 전 팟 프리스케일) ---

export interface PrescaleResponse {
  status: "ready" | "partial";
  targetReplicas: number;
  readyReplicas: number;
  message: string;
}

export async function prescaleTenant(
  tenantId: string,
  targetReplicas: number,
): Promise<PrescaleResponse> {
  return request(`/api/tenants/${tenantId}/prescale`, {
    method: "POST",
    body: JSON.stringify({ targetReplicas }),
  });
}

export async function fetchPodStatus(
  tenantId: string,
): Promise<{ replicas: number; readyReplicas: number; status: string }> {
  return request(`/api/tenants/${tenantId}/pod-status`);
}

// --- Conversation Processing Status (polling) ---

export async function fetchConversationStatus(
  tenantId: string,
  convId: string,
): Promise<{ status: "processing" | "completed" | "failed"; startedAt?: string; completedAt?: string; error?: string }> {
  return request(`/api/tenants/${tenantId}/conversations/${encodeURIComponent(convId)}/status`);
}

// --- Claude Code Workspace ---

export async function fetchClaudeWorkspaceFiles(tenantId: string): Promise<WorkspaceFile[]> {
  const data = await request<{ files: WorkspaceFile[] }>(`/api/tenants/${tenantId}/workspace/claude`);
  return data.files;
}

export async function fetchClaudeWorkspaceFile(tenantId: string, filename: string): Promise<WorkspaceFileContent> {
  return request<WorkspaceFileContent>(`/api/tenants/${tenantId}/workspace/claude/${filename}`);
}

export async function saveClaudeWorkspaceFile(tenantId: string, filename: string, content: string): Promise<WorkspaceFileContent> {
  return request<WorkspaceFileContent>(`/api/tenants/${tenantId}/workspace/claude/${filename}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}

// --- Claude Code Rules ---

export async function fetchClaudeRules(tenantId: string): Promise<RuleFile[]> {
  const data = await request<{ rules: RuleFile[] }>(`/api/tenants/${tenantId}/workspace/claude/rules`);
  return data.rules;
}

export async function fetchClaudeRule(tenantId: string, name: string): Promise<RuleFileContent> {
  return request<RuleFileContent>(`/api/tenants/${tenantId}/workspace/claude/rules/${encodeURIComponent(name)}`);
}

export async function saveClaudeRule(tenantId: string, name: string, content: string): Promise<RuleFileContent> {
  return request<RuleFileContent>(`/api/tenants/${tenantId}/workspace/claude/rules/${encodeURIComponent(name)}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });
}

export async function deleteClaudeRule(tenantId: string, name: string): Promise<{ name: string; deleted: boolean }> {
  return request(`/api/tenants/${tenantId}/workspace/claude/rules/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}

// --- KPI Sources ---

export async function fetchKpiSources(tenantCode: string): Promise<KpiSourcesResponse> {
  return request<KpiSourcesResponse>(`/api/tenants/${tenantCode}/kpi-sources`);
}

export async function fetchKpiSourceDetail(tenantCode: string, pvcCd: string): Promise<KpiSourceDetail> {
  return request<KpiSourceDetail>(`/api/tenants/${tenantCode}/kpi-sources/${encodeURIComponent(pvcCd)}`);
}

export async function syncKpiSources(tenantCode: string): Promise<{ success: boolean; fileSize: number; sourceCount: number }> {
  return request(`/api/tenants/${tenantCode}/kpi-sources/sync`, { method: "POST" });
}

export async function fetchConvertHistory(tenantCode: string): Promise<ConvertHistoryResponse> {
  return request<ConvertHistoryResponse>(`/api/tenants/${tenantCode}/convert-history`);
}

// --- Tenant Admin ---

export async function fetchTenantConfigs(): Promise<TenantConfig[]> {
  const data = await request<{ tenants: TenantConfig[] }>("/api/admin/tenants");
  return data.tenants;
}

export async function createTenant(body: Omit<TenantConfig, "default">): Promise<TenantConfig> {
  const data = await request<{ success: boolean; tenant: TenantConfig }>("/api/admin/tenants", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data.tenant;
}

export async function updateTenant(id: string, body: Partial<TenantConfig>): Promise<TenantConfig> {
  const data = await request<{ success: boolean; tenant: TenantConfig }>(`/api/admin/tenants/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return data.tenant;
}

export async function deleteTenant(id: string): Promise<{ deleted: string }> {
  return request(`/api/admin/tenants/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
